import {
  FintracFlattenedFormFieldConfig,
  FintracFlattenedFormFieldConfigs,
  FintracForm,
  FintracFormField,
  FintracFormFields,
  FintracFormFieldSection,
  FintracFormFlags,
  FintracFormSection,
  FintracFormValueOrArray,
  FintracStrFilingContent,
} from 'app/modules/fintracStr/models';
import {
  isFieldBlank,
  isFieldHidden,
} from 'app/modules/fintracStr/renderConditions';
import { isArray, isEqual, isObject, isUndefined } from 'lodash';
import { flattenValues } from 'app/modules/fintracStr/formUtils';

/**
 * Extracts and structures form section values from initial data based on field configuration.
 *
 * This function serves as the primary entry point for transforming form data into structured
 * objects or arrays, depending on whether the section is repeatable or not.
 *
 * **For non-repeatable sections:**
 * - Returns a single object containing all field values for the section
 * - Uses field configuration to determine which values to extract and how to structure them
 *
 * **For repeatable sections:**
 * - Returns an array of objects, each representing one instance of the repeatable section
 * - Determines array size based on existing data in the section path
 * - Ensures at least one entry exists in the result array (minimum length of 1)
 * - Each array element contains the same field structure as defined in the configuration
 *
 * The function internally flattens the initial values for efficient field lookup and
 * delegates the actual value extraction to `getSectionFieldValues`.
 *
 * @param tabPath - The dot-notation path to the section in the form data (e.g., 'reportDetails', 'transactions')
 * @param fields - Array of field configurations that define the section's structure and validation rules
 * @param initialValues - The raw form data object containing all section and field values
 * @param repeatable - Optional boolean flag indicating if this section supports multiple instances
 * @returns For non-repeatable sections: object with field values. For repeatable sections: array of objects
 *
 * @example
 * // Non-repeatable section
 * getSectionValues('reportDetails', reportFields, data, false)
 * // Returns: { reportingEntityNumber: '123', reportDate: '2024-01-01' }
 *
 * // Repeatable section
 * getSectionValues('transactions', transactionFields, data, true)
 * // Returns: [{ amount: 1000, currency: 'CAD' }, { amount: 2000, currency: 'USD' }]
 */
export const getSectionValues = (
  tabPath: string,
  fields: FintracFormFields,
  initialValues: Record<string, any>,
  repeatable?: boolean,
  rawDefaultNumOfEntries?: FintracFormFlags['defaultNumOfEntries'],
) => {
  const computedDefaultNumOfEntries =
    typeof rawDefaultNumOfEntries === 'function'
      ? rawDefaultNumOfEntries()
      : rawDefaultNumOfEntries;

  if (
    isUndefined(initialValues[tabPath]) &&
    computedDefaultNumOfEntries === 0
  ) {
    return undefined;
  }

  const flattenInitialValues = flattenValues(initialValues);
  const currInitialValues = initialValues[tabPath] ?? (repeatable ? [] : {});

  if (repeatable) {
    const allValues: FintracFormValueOrArray[] = [];

    const maxLength = Math.max(
      currInitialValues.length,
      computedDefaultNumOfEntries ?? 1,
    ); // Note currInitialValues will always be an array if repeatable is true

    for (let i = 0; i < maxLength; i++) {
      allValues.push(
        getSectionFieldValues(
          `${tabPath}[${i}]`,
          fields,
          flattenInitialValues,
          [i],
        ),
      );
    }

    return allValues;
  }

  return getSectionFieldValues(tabPath, fields, flattenInitialValues, []);
};

/**
 * Extracts field values from a section based on the provided configuration.
 *
 * This function processes a section's field configuration to construct a structured
 * object containing all field values. It handles both regular fields and repeatable
 * fields (arrays of objects) appropriately.
 *
 * @param tabPath - The base path for the section (e.g., 'reportDetails' or 'transactions')
 * @param fields - Array of field configurations defining the structure and validation rules
 * @param flattenInitialValues - Flattened object containing all available form values with dot-notation keys
 * @returns A structured object containing all field values for the section
 */
const getSectionFieldValues = (
  tabPath: string,
  fields: FintracFormFields,
  flattenInitialValues: Record<string, any>,
  pathIndexes: number[],
): FintracFormValueOrArray => {
  const fieldValues: Record<string, any> = {};
  const currInitialValues: Record<string, any> =
    flattenInitialValues[tabPath] ?? {};
  const [sectionIndex, ...fieldSectionIndex] = pathIndexes;

  fields.forEach((fieldConfig) => {
    // Handle section fields
    if (!isArray(fieldConfig) && fieldConfig.type === 'section') {
      if (fieldConfig.repeatable) {
        const allValues: FintracFormValueOrArray[] = [];

        const maxLength = Math.max(
          currInitialValues[fieldConfig.name]?.length ?? 0,
          typeof fieldConfig.defaultNumOfEntries === 'function'
            ? fieldConfig.defaultNumOfEntries(
                undefined,
                flattenInitialValues,
                sectionIndex,
                fieldSectionIndex,
              )
            : (fieldConfig.defaultNumOfEntries ?? 1),
        );

        for (let i = 0; i < maxLength; i++) {
          allValues.push(
            getSectionFieldValues(
              `${tabPath}.${fieldConfig.name}[${i}]`,
              fieldConfig.fields,
              flattenInitialValues,
              pathIndexes.concat(i),
            ),
          );
        }

        fieldValues[fieldConfig.name] = allValues;
        return;
      }

      // Handle non-repeatable fields within a section
      const nonRepeatableFieldValues = getSectionFieldValues(
        `${tabPath}.${fieldConfig.name}`,
        fieldConfig.fields,
        flattenInitialValues,
        pathIndexes,
      );

      const computedDefaultNumOfEntries =
        typeof fieldConfig.defaultNumOfEntries === 'function'
          ? fieldConfig.defaultNumOfEntries(
              undefined,
              flattenInitialValues,
              sectionIndex,
              fieldSectionIndex,
            )
          : (fieldConfig.defaultNumOfEntries ?? 1);

      fieldValues[fieldConfig.name] =
        computedDefaultNumOfEntries === 0 &&
        Object.values(nonRepeatableFieldValues ?? {}).every((val) =>
          isFieldBlank(val),
        )
          ? undefined
          : nonRepeatableFieldValues;
      return;
    }

    // Handle array fields
    if (isArray(fieldConfig)) {
      for (const rowConfig of fieldConfig) {
        Object.assign(
          fieldValues,
          getFieldObject(
            `${tabPath}.${rowConfig.name}`,
            tabPath,
            flattenInitialValues,
            rowConfig,
            pathIndexes,
          ),
        );
      }
      return;
    }

    // Handle regular fields
    Object.assign(
      fieldValues,
      getFieldObject(
        `${tabPath}.${fieldConfig.name}`,
        tabPath,
        flattenInitialValues,
        fieldConfig,
        pathIndexes,
      ),
    );
  });

  return fieldValues;
};

const getFieldObject = (
  path: string,
  previousPath: string,
  flattenInitialValues: Record<string, FintracFormValueOrArray>,
  defaultConfig: FintracFormField,
  pathIndexes: number[],
): Record<string, FintracFormValueOrArray> => {
  if (defaultConfig.payloadToFormValueConversion) {
    return defaultConfig.payloadToFormValueConversion(
      previousPath,
      flattenInitialValues,
      defaultConfig,
    );
  }

  return {
    [defaultConfig.name]: getFieldValue(
      path,
      flattenInitialValues,
      defaultConfig,
      pathIndexes,
    ),
  };
};

/**
 * Extracts a field value from flattened initial values, handling both regular and repeatable fields.
 *
 * For regular fields:
 * - Returns the value at the specified path, or the field's initial value if not found
 *
 * For repeatable fields (when `defaultConfig.repeatable` is true):
 * - Builds an array by iterating through indexed entries (path[0], path[1], etc.)
 * - Continues iteration while values exist at indexed paths
 * - Uses the field's initial value as fallback for missing indexed entries
 * - Ensures at least one entry exists in the result array (even if undefined)
 * - Stops when a falsy value is encountered and at least one value has been collected
 *
 * @param path - The dot-notation path to the field in the flattened values (e.g., 'transactions.amount')
 * @param flattenInitialValues - Flattened object containing all form values with dot-notation keys
 * @param defaultConfig - Field configuration object containing metadata like repeatability and initial values
 * @returns For regular fields: single value or initialValue. For repeatable fields: array of values
 *
 * @example
 * // Regular field
 * getFieldValue('reportDetails.entityNumber', flatValues, { repeatable: false, initialValue: undefined })
 * // Returns: '12345' or undefined if not found
 *
 * // Repeatable field
 * getFieldValue('transactions.amount', flatValues, { repeatable: true, initialValue: 0 })
 * // Returns: [1000, 2000, 0] (array with values and fallbacks)
 */
const getFieldValue = (
  path: string,
  flattenInitialValues: Record<string, FintracFormValueOrArray>,
  defaultConfig: FintracFormField,
  pathIndexes: number[],
): FintracFormValueOrArray => {
  const [sectionIndex, ...fieldSectionIndex] = pathIndexes;

  const defaultValue =
    typeof defaultConfig.defaultValue === 'function'
      ? defaultConfig.defaultValue()
      : defaultConfig.defaultValue;

  const isRequired =
    typeof defaultConfig.required === 'function'
      ? defaultConfig.required(
          flattenInitialValues,
          sectionIndex,
          fieldSectionIndex,
        )
      : defaultConfig.required;

  if (defaultConfig.repeatable) {
    const computedDefaultNumOfEntries =
      typeof defaultConfig.defaultNumOfEntries === 'function'
        ? defaultConfig.defaultNumOfEntries(
            isRequired,
            flattenInitialValues,
            sectionIndex,
            fieldSectionIndex,
          )
        : (defaultConfig.defaultNumOfEntries ?? 1); // If defaultNumOfEntries is not specified, we default the minimum number of entries to 1

    const allValues: any[] = []; // This is supposed to be FintracFormValueOrArray[], but typescript is not happy if type as FintracFormValueOrArray[] since its a nested type

    for (let index = 0; ; index++) {
      const currPath = `${path}[${index}]`;

      if (
        currPath in flattenInitialValues || // We always include the value that was provided. We are assuming that if the path exist in flattenValues, the value is provided.
        allValues.length < computedDefaultNumOfEntries // We include the default value until the specified number of entries is reached.
      ) {
        allValues.push(flattenInitialValues[currPath] ?? defaultValue);
      } else {
        break;
      }
    }

    return allValues;
  }

  return flattenInitialValues[path] ?? defaultValue;
};

export const addFlattenedFormFieldConfig = (
  acc: Record<string, FintracFlattenedFormFieldConfigs>,
  path: string,
  config: FintracFormField | FintracFormSection | FintracFormFieldSection,
): void => {
  const baseConfig: FintracFlattenedFormFieldConfig = {
    label: config.label,
    repeatable: config.repeatable,
    type: config.type,
    hideIfTrue: config.hideIfTrue,
    mustExistInPayload: config.mustExistInPayload,
    mustHaveValueIfExist: config.mustHaveValueIfExist,
    payloadToFormValueConversion:
      'payloadToFormValueConversion' in config
        ? config.payloadToFormValueConversion
        : undefined, // Tab sections does not convert form values
    formToPayloadValueConversion:
      'formToPayloadValueConversion' in config
        ? config.formToPayloadValueConversion
        : undefined, // Tab sections does not convert form values
    payloadErrorPathToFormErrorPathConversion:
      'payloadErrorPathToFormErrorPathConversion' in config
        ? config.payloadErrorPathToFormErrorPathConversion
        : undefined,
  };

  // Ensure internal field paths remain single-config and do not get overwritten inadvertently.
  acc[path] = acc[path] ?? [baseConfig];

  // This is needed so that the error paths given in the payload have a field to map to.
  // Without this, the error will not be shown since it dosent know which field to target.
  // Note that this relationship is a one to many relationship, which means one error paths can map to multiple form fields
  if (baseConfig.payloadErrorPathToFormErrorPathConversion) {
    const { payloadPath } =
      baseConfig.payloadErrorPathToFormErrorPathConversion([], true);

    for (const ppath of payloadPath) {
      const existing = acc[ppath] ?? [];
      // Avoid self-referential duplicates that would create multi-configs for internal fields and avoid duplicate references
      // This comparison works since the baseConfig reference is the same for all instances within the acc array
      if (
        ppath !== path &&
        !existing.some((existingConfig) => isEqual(existingConfig, baseConfig))
      ) {
        acc[ppath] = [...existing, baseConfig];
      }
    }
  }
};

/**
 * Flattens the FINTRAC_STR_FORM_FIELDS configuration into a single-level object.
 * This function converts the nested form structure into a flat object where:
 * - Keys are full paths to fields (e.g., 'transactions[0].amount' or 'reportDetails.reportingEntityNumber')
 * - Values are the corresponding field configurations
 * - Repeatable sections use [*] notation in paths
 * - Nested arrays are flattened into a single level
 *
 * Example:
 * Input: [{ tabKey: 'reportDetails', tabSection: { fields: [{ name: 'reportingEntityNumber' }] } }]
 * Output: { 'reportDetails.reportingEntityNumber': { name: 'name', ... } }
 *
 * @param formFields - Array of form configurations containing tab sections and their fields
 * @returns A flat object mapping field paths to their configurations
 */
export const flattenFormConfig = (
  formFields: FintracForm[],
): Record<string, FintracFlattenedFormFieldConfigs> => {
  const result: Record<string, FintracFlattenedFormFieldConfigs> = {};

  for (const { tabSection } of formFields) {
    const { name, repeatable, fields } = tabSection;
    const path = repeatable ? `${name}[*]` : name;

    flattenFields(fields, result, path);
    addFlattenedFormFieldConfig(result, path, tabSection);
  }

  return result;
};

const flattenFields = (
  fields: FintracFormFields,
  accumulator: Record<string, FintracFlattenedFormFieldConfigs>,
  path: string = '',
): void => {
  for (const field of fields) {
    if (isArray(field)) {
      for (const f of field) {
        const fieldPath = `${path}.${f.name}`;
        const repeatablePath = f.repeatable ? `${fieldPath}[*]` : fieldPath;
        addFlattenedFormFieldConfig(accumulator, repeatablePath, f);
      }
    } else if (field.type === 'section') {
      const newPath = path ? `${path}.${field.name}` : field.name;
      const repeatablePath = field.repeatable ? `${newPath}[*]` : newPath;
      addFlattenedFormFieldConfig(accumulator, repeatablePath, field);
      flattenFields(field.fields, accumulator, repeatablePath);
    } else {
      const newPath = path ? `${path}.${field.name}` : field.name;
      const repeatablePath = field.repeatable ? `${newPath}[*]` : newPath;
      addFlattenedFormFieldConfig(accumulator, repeatablePath, field);
    }
  }
};

/**
 * Processes form values based on their configuration settings.
 * This function handles three main scenarios:
 * 1. Fields that must exist in payload and have a value
 * 2. Fields that must exist in payload but don't need a value
 * 3. Fields that don't need to exist in payload
 *
 * The function ensures that:
 * - Fields with mustExistInPayload=true and mustHaveValueIfExist=true are included only if they have a value
 * - Fields with mustExistInPayload=true and mustHaveValueIfExist=false are included regardless of value
 * - Fields with mustExistInPayload=false are included only if they have a value
 *
 * NOTE: This function mutates the accumulator object.
 *
 * @param acc - The accumulator object
 * @param config - The field configuration
 * @param value - The field value
 * @param key - The field key
 * @returns The updated accumulator object with the processed field
 */
export const getFormValues = (
  acc: Record<string, FintracFormValueOrArray>,
  configs: FintracFlattenedFormFieldConfigs,
  value: FintracFormValueOrArray,
  key: string,
  formValues: FintracStrFilingContent,
  flattenedValues: Record<string, any>,
  indexes: number[],
  filterEmptyValues: boolean = true,
): Record<string, FintracFormValueOrArray> => {
  if (configs.length > 1) {
    // This is an invalid state since ALL internal field should only have one config associated with it
    // (Internal fields are fields that are rendered within a form, excluding fields that are needed for submission but not required for validation)
    // (E.g. onlyDateOfPosting, datetimeOfPosting are internal fields while dateOfPosting is an external field since it does not exist as one of the fields)
    return acc;
  }

  const config = configs[0];

  if (indexes.length > 0 && config.hideIfTrue) {
    const [currSectionIndex, ...currFieldSectionIndex] = indexes;

    if (
      isFieldHidden(
        config.hideIfTrue,
        flattenedValues,
        currSectionIndex,
        currFieldSectionIndex,
      )
    ) {
      return acc;
    }
  }

  const filteredValue =
    isArray(value) && filterEmptyValues
      ? value.filter((item) => !isFieldBlank(item))
      : value;

  if (config.mustExistInPayload && config.mustHaveValueIfExist) {
    // This field should not be blank. If it is,further validation and error throwing will be done by backend
    return formatValues(acc, config, filteredValue, key, formValues, indexes);
  }

  if (config.mustExistInPayload && !config.mustHaveValueIfExist) {
    return formatValues(acc, config, filteredValue, key, formValues, indexes);
  }

  if (!config.mustExistInPayload && config.mustHaveValueIfExist) {
    if (isFieldBlank(filteredValue)) {
      return acc;
    }

    return formatValues(acc, config, filteredValue, key, formValues, indexes);
  }

  if (!config.mustExistInPayload && !config.mustHaveValueIfExist) {
    if (isFieldBlank(filteredValue)) {
      return acc;
    }

    return formatValues(acc, config, filteredValue, key, formValues, indexes);
  }

  return acc;
};

const formatValues = (
  acc: Record<string, FintracFormValueOrArray>,
  config: FintracFlattenedFormFieldConfig,
  values: FintracFormValueOrArray,
  key: string,
  formValues: FintracStrFilingContent,
  indexes: number[],
): Record<string, FintracFormValueOrArray> => {
  if (config.formToPayloadValueConversion) {
    return {
      ...acc,
      ...config.formToPayloadValueConversion(values, formValues, indexes),
    }; // We are using spread here to allow formToPayloadValueConversion to follow functional programming principles despite being slightly less performant
  }

  acc[key] = values;

  return acc;
};

export const getFormSectionValues = (
  acc: Record<string, FintracFormValueOrArray>,
  configs: FintracFlattenedFormFieldConfigs,
  value: any,
  key: string,
  flattenedValues: Record<string, any>,
  indexes: number[],
): Record<string, FintracFormValueOrArray> => {
  if (configs.length > 1) {
    // This is an invalid state since ALL sections should only have one config associated with it
    return acc;
  }

  const config = configs[0];

  if (indexes.length > 0 && config.hideIfTrue) {
    const [currSectionIndex, ...currFieldSectionIndex] = indexes;

    if (
      isFieldHidden(
        config.hideIfTrue,
        flattenedValues,
        currSectionIndex,
        currFieldSectionIndex,
      )
    ) {
      return acc;
    }
  }

  const filteredValue = (() => {
    if (isObject(value)) {
      return Object.entries(value).reduce(
        (innerAcc, [innerKey, innerValue]) =>
          !isFieldBlank(innerValue)
            ? { ...innerAcc, [innerKey]: innerValue }
            : innerAcc,
        {},
      );
    }

    if (isArray(value)) {
      return value.filter((item) => !isFieldBlank(item));
    }

    return value;
  })();

  if (config.mustExistInPayload && config.mustHaveValueIfExist) {
    // This field should not be blank. If it is,further validation and error throwing will be done by backend
    acc[key] = value;
    return acc;
  }

  if (config.mustExistInPayload && !config.mustHaveValueIfExist) {
    acc[key] = value;
    return acc;
  }

  if (!config.mustExistInPayload && config.mustHaveValueIfExist) {
    if (isFieldBlank(filteredValue)) {
      return acc;
    }

    acc[key] = value;
    return acc;
  }

  if (!config.mustExistInPayload && !config.mustHaveValueIfExist) {
    if (isFieldBlank(filteredValue)) {
      return acc;
    }

    acc[key] = value;
    return acc;
  }

  return acc;
};
